import React, { useMemo, useEffect, useState, useRef } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useLanguage } from '../../../../../../../contexts/LanguageContext';
import { useGetExternalPartsQuery } from '../../../../../../../api';

const ExternalPartsDropdown = ({ value, dealerId, dealerAddressId, onChange, className, disabled }) => {
    const { th } = useLanguage();
    const [selectedItem, setSelectedItem] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [filterValue, setFilterValue] = useState(''); // Custom filter input için
    const debounceTimerRef = useRef(null);

    const { data: externalPartsResponse, isLoading } = useGetExternalPartsQuery(
        {
            PageSize: 20,
            PageNumber: 1,
            dealerId,
            dealerAddressId,
            SearchText: searchText
        },
        {
            skip: !dealerId || !dealerAddressId
        }
    );

    const options = useMemo(() => {
        const items = externalPartsResponse?.data?.items ?? [];
        return items.map((x) => ({
            itemCode: x.itemCode,
            title: x.itemCodeDesc,
            displayValue: `${x.itemCode} - ${x.itemCodeDesc}`,
            quantity: x.quantity ?? 0,
            itemPrice: x.itemPrice,
            itemPriceCurr: x.itemPriceCurr
        }));
    }, [externalPartsResponse]);

    // ItemCodeDropdown gibi: value string/object gelebilir → selectedItem state'ini oturt
    useEffect(() => {
        if (!value) {
            setSelectedItem(null);
            return;
        }

        if (typeof value === 'object') {
            // FE’de object taşıyorsan direkt bas
            setSelectedItem({
                ...value,
                displayValue: value.displayValue ?? `${value.itemCode} - ${value.title ?? value.itemCodeDesc ?? ''}`
            });
            return;
        }

        // value string ise itemCode'a göre options içinde ara
        const found = options.find((o) => o.itemCode === value) ?? null;

        if (found) {
            setSelectedItem(found);
        } else {
            // seçenek listesinde yoksa fallback (ItemCodeDropdown mantığı)
            setSelectedItem({
                itemCode: value,
                title: value,
                displayValue: `${value} - ${value}`,
                quantity: 0
            });
        }
    }, [value, options]);

    const handleSelectionChange = (e) => {
        setSelectedItem(e.value);

        // Seçim yapıldığında sadece görsel filter'ı temizle
        // searchText'i temizleme - gereksiz API çağrısına neden olur
        setFilterValue('');

        // Bekleyen timer varsa iptal et
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        onChange?.(e.value); // PartFormDialog zaten object bekliyor, quantity vs. için iyi
    };

    // Custom filter input onChange - Debounce ile API çağrısı
    const handleFilterInputChange = (value) => {
        // Input'ta hemen görünsün
        setFilterValue(value);

        // Önceki timer'ı iptal et
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // 600ms sonra API çağrısı yap
        debounceTimerRef.current = setTimeout(() => {
            setSearchText(value);
        }, 600);
    };

    // Custom filter template - Dropdown'ın kendi filter'ını değil, bizim controlled input'umuzu kullan
    const filterTemplate = () => (
        <div className="p-dropdown-filter-container">
            <InputText
                value={filterValue}
                onChange={(e) => handleFilterInputChange(e.target.value)}
                className="p-dropdown-filter w-full"
                placeholder={th('common.search')}
                autoFocus
                type="text"
                inputMode="text"
            />
            <span className="p-dropdown-filter-icon pi pi-search" />
        </div>
    );

    // Cleanup: Component unmount olduğunda timer'ı temizle
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    return (
        <Dropdown
            disabled={disabled}
            value={selectedItem}
            options={options}
            onChange={handleSelectionChange}
            optionLabel="displayValue" // ✅ inputta "CODE - DESC" görünsün
            filter
            filterTemplate={filterTemplate}
            showClear
            className={className}
            placeholder={th?.('common.itemCodeSelectPlaceholder') ?? 'Select...'}
            loading={isLoading}
            emptyFilterMessage={th?.('common.noResultsFound') ?? 'No results found'}
            itemTemplate={(option) => (
                <div>
                    <span style={{ fontWeight: 'bold' }}>{option.itemCode}</span> - {option.title}
                </div>
            )}
        />
    );
};

export default ExternalPartsDropdown;
